/**
 * /dashboard/posh/certificates — My certificates.
 *
 * Migration note (2026-06-01, Next.js → Vite, Phase 3d): port of
 * frontend/src/app/(dashboard)/dashboard/posh/certificates/page.tsx.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Award,
  Download,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { localize, poshApi, toApiError } from "@/lib/api";
import { useCertificates, useTrainingModules } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/certificates",
)({
  component: CertificatesRoute,
});

function CertsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="mt-2 h-4 w-1/3" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-9 w-40" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function CertificatesList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const certsQ = useCertificates();
  const modulesQ = useTrainingModules();
  const [downloading, setDownloading] = useState<number | null>(null);

  const moduleTitle = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of modulesQ.data?.modules ?? []) {
      map.set(m.module.id, localize(m.module.title) || m.module.code);
    }
    return map;
  }, [modulesQ.data]);

  if (certsQ.isLoading) return <CertsSkeleton />;

  if (certsQ.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load your certificates</AlertTitle>
        <AlertDescription>
          {toApiError(certsQ.error).message} Please refresh to try again.
        </AlertDescription>
      </Alert>
    );
  }

  const certs = [...(certsQ.data?.certificates ?? [])].sort(
    (a, b) =>
      new Date(b.issued_at).getTime() - new Date(a.issued_at).getTime(),
  );

  if (certs.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Award className="h-6 w-6" />
          </span>
          <p className="max-w-sm text-sm text-muted-foreground">
            You have not earned any certificates yet. Pass a module quiz
            in{" "}
            <Link
              className="font-medium text-primary underline"
              to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}
            >
              POSH Training
            </Link>{" "}
            to earn one.
          </p>
        </CardContent>
      </Card>
    );
  }

  const onDownload = async (id: number) => {
    setDownloading(id);
    try {
      const blob = await poshApi.downloadCertificatePdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `workright-certificate-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const e = toApiError(err);
      toast.error(
        e.error === "certificate_revoked"
          ? "This certificate has been revoked and can no longer be downloaded."
          : e.message,
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-3">
      {certs.map((c) => {
        const title =
          moduleTitle.get(c.module_id) ?? c.certificate_number;
        const verifyHref = `/verify/${c.cert_uid}`;
        return (
          <Card key={c.id}>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Award className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">{title}</span>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {c.certificate_number} · Issued{" "}
                    {fmtDate(c.issued_at)}
                    {c.valid_until
                      ? ` · Valid until ${fmtDate(c.valid_until)}`
                      : ""}
                  </CardDescription>
                </div>
                <StatusBadge
                  status={c.is_revoked ? "revoked" : "valid"}
                />
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                onClick={() => void onDownload(c.id)}
                disabled={c.is_revoked || downloading === c.id}
                className="gap-1.5"
              >
                {downloading === c.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download PDF
              </Button>
              <Button asChild variant="outline" className="gap-1.5">
                <a
                  href={verifyHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                  Public verification
                </a>
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function CertificatesRoute() {
  return (
    <PoshModuleShell
      title="My Certificates"
      description="Certificates you have earned. Download the PDF or share the public verification link."
    >
      <PoshFeatureGate>
        <CertificatesList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
